// inv/src/components/HeaderMenu.jsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Mail, Menu as MenuIcon, X, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button.jsx';
import googleLogo from '../../pages/authentication/google-logo.svg';
import MenuClose from '../../icons/menuClose.svg';
import LanguageSwitcher from '../LanguageSwitcher.jsx';
import AuthenticatedMenu from '../authenticated-menu/AuthenticatedMenu.jsx';
import { cn } from '../../lib/utils';

const HeaderMenu = ({
    menuItems,
    prefix,
    locationPathname,
    currentLanguage,
    changeLanguage,
    loading,
    authenticated,
    user,
    handleLogoutAndUpdateUser,
    domain,
    languagePrefixes,
    onSignUp,
    signUpLabel
}) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const navItemBase =
        'inline-flex items-center gap-2 h-9 px-3 rounded-md text-sm ' +
        'text-stone-950 hover:bg-stone-100 ' +
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400/70';

    const linkClasses = (active) =>
        [navItemBase, active ? 'bg-stone-100' : ''].join(' ');

    const mobileItemBase = [
        navItemBase,
        'text-xl font-medium w-full justify-start appearance-none',
    ].join(' ');

    const isPathActive = (path) => locationPathname === `${prefix}${path}`;

    const isDropdownActive = (item) =>
        item.items?.some((child) => isPathActive(child.path));

    return (
        <>
            {/* burger btn (mobile) */}
            <button
                className="block md:hidden cursor-pointer"
                onClick={() => setIsMobileMenuOpen((prev) => !prev)}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
                aria-label="Toggle menu"
            >
                {isMobileMenuOpen ? (
                    <X className="w-6 h-6 text-stone-900" aria-hidden="true" />
                ) : (
                    <MenuIcon className="w-6 h-6 text-stone-900" aria-hidden="true" />
                )}
            </button>

            {/* DESKTOP MENU */}
            <ul className="hidden md:flex items-center gap-2">
                {menuItems.map((item) => {
                    if (item.type === 'dropdown') {
                        const active = isDropdownActive(item);

                        return (
                            <li key={item.label}>
                                <DropdownMenu.Root>
                                <DropdownMenu.Trigger
  className={cn(linkClasses(active), 'cursor-pointer group')}
  aria-haspopup="menu"
>
  <span>{item.label}</span>
  <ChevronDown
    className="
      h-4 w-4
      transition-transform duration-150
      group-data-[state=open]:rotate-180
    "
  />
</DropdownMenu.Trigger>

                                    <DropdownMenu.Portal>
                                        <DropdownMenu.Content
                                            className={cn(
                                                'z-50 mt-1 min-w-[180px] rounded-lg border border-stone-200',
                                                'bg-white p-1.5 shadow-lg',
                                                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                                                'data-[state=open]:fade-in data-[state=closed]:fade-out',
                                                'data-[side=bottom]:slide-in-from-top-1'
                                            )}
                                            side="bottom"
                                            sideOffset={6}
                                            align="start"
                                            avoidCollisions
                                            collisionPadding={10}
                                        >
                                            {item.items?.map((child) => {
                                                const childActive = isPathActive(child.path);
                                                return (
                                                    <DropdownMenu.Item
                                                        key={child.path}
                                                        asChild
                                                        className={cn(
                                                            'my-0.5 flex w-full items-center rounded-md px-2 py-1.5',
                                                            'text-sm text-stone-950 outline-none cursor-pointer',
                                                            'hover:bg-stone-100'
                                                        )}
                                                    >
                                                        <Link
                                                            to={`${prefix}${child.path}`}
                                                            aria-current={childActive ? 'page' : undefined}
                                                        >
                                                            {child.label}
                                                        </Link>
                                                    </DropdownMenu.Item>
                                                );
                                            })}
                                        </DropdownMenu.Content>
                                    </DropdownMenu.Portal>
                                </DropdownMenu.Root>
                            </li>
                        );
                    }

                    // regular link
                    const active = isPathActive(item.path);
                    return (
                        <li key={item.path}>
                            <Link
                                to={`${prefix}${item.path}`}
                                className={linkClasses(active)}
                                aria-current={active ? 'page' : undefined}
                            >
                                {item.label}
                            </Link>
                        </li>
                    );
                })}

                {/* language switcher */}
                <li>
                    <LanguageSwitcher
                        currentLanguage={currentLanguage}
                        changeLanguage={changeLanguage}
                        triggerClassName={navItemBase}
                    />
                </li>

                {/* auth / signup */}
                {loading ? (
                    <li>
                        <div className="animate-pulse h-6 w-24 bg-stone-300 rounded" />
                    </li>
                ) : authenticated ? (
                    <AuthenticatedMenu
                        user={user}
                        handleLogoutAndUpdateUser={handleLogoutAndUpdateUser}
                        domain={domain}
                        currentLanguage={currentLanguage}
                        languagePrefixes={languagePrefixes}
                        itemClassName={navItemBase}
                    />
                ) : (
                    <li className="ml-2">
                        <Button
                            onClick={onSignUp}
                            size="sm"
                            variant="default"
                            className={navItemBase + ' h-9 px-3'}
                            iconLeft={
                                <>
                                    <Mail className="h-4 w-4 mr-1" />
                                    <img
                                        src={googleLogo}
                                        alt="Google logo"
                                        className="ml-1 h-4 w-4"
                                    />
                                </>
                            }
                        >
                            {signUpLabel}
                        </Button>
                    </li>
                )}
            </ul>

            {/* MOBILE OVERLAY */}
            {isMobileMenuOpen && (
                <div
                    id="mobile-menu"
                    className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-center transition-transform duration-300"
                >
                    <button
                        className="absolute top-6 right-6 cursor-pointer"
                        onClick={() => setIsMobileMenuOpen(false)}
                        aria-label="Close menu"
                    >
                        <img src={MenuClose} alt="close" className="w-8 h-8" />
                    </button>

                    <ul className="space-y-6 text-left">
                        {menuItems.map((item) => {
                            if (item.type === 'dropdown') {
                                const active = isDropdownActive(item);

                                return (
                                    <li key={item.label}>
                                        <div
                                            className={
                                                mobileItemBase + (active ? ' bg-stone-100' : '')
                                            }
                                        >
                                            {item.label}
                                        </div>
                                        <ul className="mt-2 ml-4 space-y-2">
                                            {item.items?.map((child) => {
                                                const childActive = isPathActive(child.path);
                                                return (
                                                    <li key={child.path}>
                                                        <Link
                                                            to={`${prefix}${child.path}`}
                                                            className={
                                                                mobileItemBase +
                                                                ' text-base pl-6' +
                                                                (childActive ? ' bg-stone-100' : '')
                                                            }
                                                            onClick={() => setIsMobileMenuOpen(false)}
                                                            aria-current={
                                                                childActive ? 'page' : undefined
                                                            }
                                                        >
                                                            {child.label}
                                                        </Link>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </li>
                                );
                            }

                            const active = isPathActive(item.path);
                            return (
                                <li key={item.path}>
                                    <Link
                                        to={`${prefix}${item.path}`}
                                        className={
                                            mobileItemBase + (active ? ' bg-stone-100' : '')
                                        }
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        aria-current={active ? 'page' : undefined}
                                    >
                                        {item.label}
                                    </Link>
                                </li>
                            );
                        })}

                        {/* language */}
                        <li>
                            <LanguageSwitcher
                                currentLanguage={currentLanguage}
                                changeLanguage={(code) => {
                                    changeLanguage(code);
                                    setIsMobileMenuOpen(false);
                                }}
                                triggerClassName={mobileItemBase}
                            />
                        </li>

                        {/* auth / signup */}
                        {loading ? (
                            <li>
                                <div className="animate-pulse h-6 w-24 bg-stone-300 rounded" />
                            </li>
                        ) : authenticated ? (
                            <AuthenticatedMenu
                                user={user}
                                handleLogoutAndUpdateUser={() => {
                                    handleLogoutAndUpdateUser();
                                    setIsMobileMenuOpen(false);
                                }}
                                domain={domain}
                                currentLanguage={currentLanguage}
                                languagePrefixes={languagePrefixes}
                            />
                        ) : (
                            <li>
                                <Button
                                    onClick={() => {
                                        onSignUp();
                                        setIsMobileMenuOpen(false);
                                    }}
                                    size="sm"
                                    variant="default"
                                    iconLeft={
                                        <>
                                            <Mail className="h-4 w-4 mr-1" />
                                            <img
                                                src={googleLogo}
                                                alt="Google logo"
                                                className="ml-1 h-4 w-4"
                                            />
                                        </>
                                    }
                                >
                                    {signUpLabel}
                                </Button>
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </>
    );
};

export default HeaderMenu;
